import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "@/utils/axios";
import type {
  CoreService,
  ServiceValidationResult,
} from "@/workflow/components/configurations/interoperability/types";

/**
 * Get available Core services from CF
 */
export const useGetCoreServices = () => {
  return useQuery({
    queryKey: ["interoperability", "core-services"],
    queryFn: async (): Promise<Record<string, CoreService>> => {
      const response = await axios.get("/admin/v1/interoperability");
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });
};

/**
 * Get service endpoints for a specific service
 */
export const useGetServiceEndpoints = (serviceId: string) => {
  return useQuery({
    queryKey: ["interoperability", "service-endpoints", serviceId],
    queryFn: async () => {
      const response = await axios.get(`/admin/v1/interoperability`);
      const data = response.data;
      const servicesData = data.data || {};
      const service = servicesData[serviceId];

      console.log("Service data for", serviceId, ":", service);

      if (!service) return [];

      // Extract endpoints from the service (exclude is_active)
      const endpoints = Object.keys(service).filter(
        (key) => key !== "is_active",
      );
      console.log("Available endpoints:", endpoints);

      return endpoints
        .filter((endpointName) => {
          const endpoint = service[endpointName];
          return endpoint?.is_node_supported === true;
        })
        .map((endpointName) => ({
          id: endpointName,
          name:
            endpointName === "personal_information"
              ? "Información Personal"
              : endpointName
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase()),
          service: service[endpointName],
        }));
    },
    enabled: !!serviceId,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Validate service availability (healthcheck)
 */
export const useValidateService = () => {
  return useMutation({
    mutationFn: async ({
      serviceId,
      endpointId,
    }: {
      serviceId: string;
      endpointId: string;
    }): Promise<ServiceValidationResult> => {
      const response = await axios.post(
        "/admin/v1/interoperability/health-check",
        {
          service: serviceId,
          endpoint: endpointId,
        },
      );

      const data = response.data;
      return {
        isAvailable: data.data?.is_available || false,
        responseTime: Date.now(), // You can add actual response time measurement if needed
        errorMessage: data.success ? undefined : data.message,
        lastChecked: new Date().toISOString(),
      };
    },
  });
};

/**
 * Get interoperability node configuration
 */
